import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import { useTranslation } from 'react-i18next';

import { displayToastError } from '@/helpers/display-toast';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { logger } from '@/utils/logger/logger';

import { NOTIFICATION_PROMPT_OPTED_OUT_STORAGE_KEY } from '../constants/notification-storage-keys';
import notificationService from '../firebase-notification.service';

const TAG = 'usePushNotificationsSetting:';

export const PushNotificationsStatuses = {
  Enabled: 'enabled',
  Disabled: 'disabled',
  Blocked: 'blocked',
  Unsupported: 'unsupported',
} as const;

export type PushNotificationsStatus =
  (typeof PushNotificationsStatuses)[keyof typeof PushNotificationsStatuses];

const readBrowserPermission = (): NotificationPermission | null =>
  notificationService.isSupported() ? Notification.permission : null;

const subscribeToRegistration = (listener: () => void) =>
  notificationService.subscribeToRegistration(listener);

const getIsRegistered = () => notificationService.getToken() !== null;

const getStatus = ({
  permission,
  isRegistered,
}: {
  permission: NotificationPermission | null;
  isRegistered: boolean;
}): PushNotificationsStatus => {
  if (permission === null) return PushNotificationsStatuses.Unsupported;
  if (isRegistered) return PushNotificationsStatuses.Enabled;
  if (permission === 'denied') return PushNotificationsStatuses.Blocked;
  return PushNotificationsStatuses.Disabled;
};

export const usePushNotificationsSetting = () => {
  const { t } = useTranslation('authenticated.notifications');
  const [permission, setPermission] = useState(readBrowserPermission);
  const isRegistered = useSyncExternalStore(subscribeToRegistration, getIsRegistered);
  const [, setIsOptedOut] = useLocalStorage<boolean>(
    NOTIFICATION_PROMPT_OPTED_OUT_STORAGE_KEY,
    false
  );
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    const syncPermission = () => setPermission(readBrowserPermission());
    window.addEventListener('focus', syncPermission);
    return () => window.removeEventListener('focus', syncPermission);
  }, []);

  const enable = useCallback(async () => {
    setIsPending(true);
    try {
      await notificationService.requestPermissionAndInitialize();
    } catch (error) {
      logger.error(TAG, 'Failed to enable push notifications', error);
    }
    setPermission(readBrowserPermission());
    setIsPending(false);

    if (Notification.permission !== 'granted') return;

    // Registration can fail even with permission granted (e.g. no Push API in incognito)
    const isNowRegistered = getIsRegistered();
    setIsOptedOut(!isNowRegistered);
    if (!isNowRegistered) displayToastError(t('pushSetting.enableFailed'));
  }, [setIsOptedOut, t]);

  const disable = useCallback(async () => {
    setIsPending(true);
    setIsOptedOut(true);
    await notificationService.unregisterDevice();
    setIsPending(false);
  }, [setIsOptedOut]);

  return { status: getStatus({ permission, isRegistered }), isPending, enable, disable };
};
